import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

interface EditDonorDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  donor: any | null;
  ptaId: string;
  onSuccess: () => void;
}

const CONTACT_METHODS = [
  { id: "email", label: "Email" },
  { id: "phone", label: "Phone" },
  { id: "in_person", label: "In Person" },
  { id: "post", label: "Post" },
];

export function EditDonorDialog({ open, onOpenChange, donor, ptaId, onSuccess }: EditDonorDialogProps) {
  const [name, setName] = useState("");
  const [contactDate, setContactDate] = useState("");
  const [contactMethods, setContactMethods] = useState<string[]>([]);
  const [responded, setResponded] = useState(false);
  const [donated, setDonated] = useState(false);
  const [donationDetails, setDonationDetails] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (donor) {
      setName(donor.name || "");
      setContactDate(donor.contact_date || "");
      setContactMethods(donor.contact_methods || []);
      setResponded(donor.responded || false);
      setDonated(donor.donated || false);
      setDonationDetails(donor.donation_details || "");
    } else {
      setName("");
      setContactDate("");
      setContactMethods([]);
      setResponded(false);
      setDonated(false);
      setDonationDetails("");
    }
  }, [donor, open]);

  const handleContactMethodToggle = (methodId: string) => {
    setContactMethods(prev =>
      prev.includes(methodId)
        ? prev.filter(m => m !== methodId)
        : [...prev, methodId]
    );
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!name.trim()) {
      toast.error("Please enter a name");
      return;
    }

    if (!contactDate) {
      toast.error("Please select a contact date");
      return;
    }

    if (contactMethods.length === 0) {
      toast.error("Please select at least one contact method");
      return;
    }

    setIsSubmitting(true);

    try {
      const { data: { user } } = await supabase.auth.getUser();

      const donorData = {
        pta_id: ptaId,
        name: name.trim(),
        contact_date: contactDate,
        contact_methods: contactMethods,
        responded,
        donated,
        donation_details: donationDetails.trim() || null,
        created_by: user?.id,
      };

      if (donor) {
        const { error } = await supabase
          .from("donors")
          .update(donorData)
          .eq("id", donor.id);

        if (error) throw error;
        toast.success("Donor updated successfully");
      } else {
        const { error } = await supabase
          .from("donors")
          .insert(donorData);

        if (error) throw error;
        toast.success("Donor added successfully");
      }

      onSuccess();
      onOpenChange(false);
    } catch (error) {
      console.error("Error saving donor:", error);
      toast.error("Failed to save donor");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <form onSubmit={handleSubmit}>
          <DialogHeader>
            <DialogTitle>{donor ? "Edit Donor" : "Add Donor"}</DialogTitle>
            <DialogDescription>
              Record business or person contact details and donation information
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="name">Business/Person Name *</Label>
              <Input
                id="name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Enter business or person name"
                required
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="contactDate">Contact Date *</Label>
              <Input
                id="contactDate"
                type="date"
                value={contactDate}
                onChange={(e) => setContactDate(e.target.value)}
                required
              />
            </div>

            <div className="space-y-2">
              <Label>Contact Methods * (select at least one)</Label>
              <div className="grid grid-cols-2 gap-4">
                {CONTACT_METHODS.map((method) => (
                  <div key={method.id} className="flex items-center space-x-2">
                    <Checkbox
                      id={method.id}
                      checked={contactMethods.includes(method.id)}
                      onCheckedChange={() => handleContactMethodToggle(method.id)}
                    />
                    <Label htmlFor={method.id} className="font-normal cursor-pointer">
                      {method.label}
                    </Label>
                  </div>
                ))}
              </div>
            </div>

            <div className="space-y-3 pt-2">
              <Label>Response & Donation Status</Label>
              
              <div className="flex items-center space-x-2">
                <Checkbox
                  id="responded"
                  checked={responded}
                  onCheckedChange={(checked) => setResponded(checked as boolean)}
                />
                <Label htmlFor="responded" className="font-normal cursor-pointer">
                  They responded
                </Label>
              </div>

              <div className="flex items-center space-x-2">
                <Checkbox
                  id="donated"
                  checked={donated}
                  onCheckedChange={(checked) => setDonated(checked as boolean)}
                />
                <Label htmlFor="donated" className="font-normal cursor-pointer">
                  They donated
                </Label>
              </div>

              {donated && (
                <div className="space-y-2 pl-6">
                  <Label htmlFor="donationDetails">What they donated *</Label>
                  <Textarea
                    id="donationDetails"
                    value={donationDetails}
                    onChange={(e) => setDonationDetails(e.target.value)}
                    placeholder="Describe what they donated..."
                    rows={3}
                    required={donated}
                  />
                  {!donationDetails.trim() && (
                    <p className="text-xs text-destructive">Please describe what they donated</p>
                  )}
                </div>
              )}
            </div>
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={isSubmitting}>
              {isSubmitting ? "Saving..." : donor ? "Update" : "Add"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}